// Castintech dashboard — global state container (TypeScript).
// Faz 9b PR4 — dashboard.html'in 41 inline `let X` deklarasyonu tek mutable
// `state` objesine taşındı. Faz 10 PR1-7'de inline handler'lar silindi,
// modüller direkt `import { state } from './state.js'` kullanıyor.
// Faz 12 PR-B — state.js → state.ts migration (gradual TS).
//
// Plan agent kararı (Faz 9b plan):
// - `let X` export'lari import sonrasi read-only oldugu icin **mutable
//   wrapper object** secildi.
// - 30+ rename yerine **getter/setter bridge** kullanildi (Faz 9b PR4-PR10);
//   PR10'da bridge sökme ile loop kaldırıldı, modüller artık `state.X` direct.
//
// Kullanim (TS):
//   import { state } from './state.js';  // .js extension korunur (esbuild plugin resolves to .ts)
//   state.auditRows.push(row);
//
// CRITICAL: Initial degerler burada set edilir. AppState tip tanımı
// types/state.ts'te.

import { REFRESH_INTERVAL } from './config.js';
import type { AppState } from '../types/state';

export const state: AppState = {
  // ── Faz 1: Pano + ekip + onaylar (12) ──
  companies: [],
  agents: [],
  tasks: [],
  approvals: [],
  fallbackStats: null,
  accounts: null,
  // Faz 52-Y W3a — açılış ekranı "Bugün" (CEO Kokpit): bekleyen onaylar +
  // council SLA + agent health tek bakışta. Pano sidebar'da bir tık uzakta.
  currentPage: 'ceo-cockpit',
  // Faz 50.A — Filter persist: boot'ta localStorage'tan restore (private mode safe).
  selectedCompanyId: (() => {
    try { return localStorage.getItem('castintech_selected_company') || 'all'; }
    catch { return 'all'; }
  })(),
  agentFilter: null,
  openIssueId: null,
  refreshTimer: null,
  countdownVal: REFRESH_INTERVAL / 1000,

  // ── Faz 2: Skills + drawer + filtreler (5) ──
  agentsFull: [],            // /castintech-api/agents/full cache
  skillsAll: [],             // /castintech-api/skills cache
  metaCacheAt: 0,
  // Faz 50.A — Pano filter state localStorage persist (search/priority/assignee/age).
  filterState: (() => {
    try {
      const raw = localStorage.getItem('castintech_filter_state');
      if (raw) {
        const parsed = JSON.parse(raw) as Record<string, string>;
        return {
          search: typeof parsed.search === 'string' ? parsed.search : '',
          priority: typeof parsed.priority === 'string' ? parsed.priority : '',
          assignee: typeof parsed.assignee === 'string' ? parsed.assignee : '',
          age: typeof parsed.age === 'string' ? parsed.age : '',
        };
      }
    } catch { /* private mode / invalid JSON */ }
    return { search: '', priority: '', assignee: '', age: '' };
  })(),
  patchInFlight: false,

  // ── Faz 3: Drawer dispatch + comment (3) ──
  dispatchInFlight: false,
  commentInFlight: false,
  niPrefill: null,           // {companyId, agentId, agentName} | null

  // ── Faz 4: Telegram tab (4) ──
  telegramSessions: [],
  tgSendInFlight: false,
  tgOpenChatId: null,
  tgOpenAgentName: null,

  // ── Faz 5: Sağlık tab + Audit feed (10) ──
  healthData: null,
  healthAccounts: null,
  auditRows: [],
  auditTitles: [],
  auditActors: [],
  auditActions: [],
  auditFilter: '',
  auditActorFilter: '',
  auditActionFilter: '',
  saglikTimer: null,

  // ── Faz 7: SSE live updates (3) ──
  sseConn: null,
  sseFallbackTimer: null,
  sseErrorCount: 0,

  // ── Faz 8b: Audit pagination (3) ──
  auditCursor: null,         // {before_ts, before_id} | null
  auditHasMore: false,
  auditLoading: false,

  // ── Faz 21 PR-B: Müşteriler kanban view (2) ──
  musteriRows: null,
  musteriMeta: null,

  // ── Faz 42 PR-E: Müşteri detail drawer (4) ──
  musteriDetailCache: {},
  musteriDrawerOpen: null,
  musteriDrawerTab: 'genel',
  musteriDrawerDecisions: {},

  // ── Faz 22 PR-B: Faturalar timeline kanban view (2) ──
  faturaRows: null,
  faturaMeta: null,

  // ── Faz 22 PR-C: Kararlar timeline view (2) ──
  kararRows: null,
  kararMeta: null,

  // ── Faz 23 PR-B: Skills usage telemetry panel (2) ──
  skillUsageRows: null,
  skillUsageMeta: null,

  // ── Faz 44 PR-B: API credentials admin paneli (2) ──
  apiCredentials: null,
  apiCredentialsMeta: null,

  // ── Misc UI (1) ──
  suggestionsVisible: false,
};

// Tüm property'lerin listesi — eski bridge.js'in getter/setter loop'u için kullanılırdı.
// Faz 9b PR10'da bridge söküldü, ama STATE_KEYS export'u kalır (test referansları için).
export const STATE_KEYS: (keyof AppState)[] = Object.keys(state) as (keyof AppState)[];
